
import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { 
  CameraInventory, 
  CameraFormValues, 
  cameraSchema,
  LensInventory,
  FilmInventory
} from "@/types/inventory";
import { safelyAssertType } from "@/utils/typedSupabase";
import { isFilmCamera, getCompatibleFilmType } from "@/utils/cameraUtils";

export function useCameraDetails(id: string | undefined) {
  const [camera, setCamera] = useState<CameraInventory | null>(null);
  const [compatibleLenses, setCompatibleLenses] = useState<LensInventory[]>([]);
  const [compatibleFilms, setCompatibleFilms] = useState<FilmInventory[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [hasAdapter, setHasAdapter] = useState(false);
  const [adapterMounts, setAdapterMounts] = useState<string[]>([]);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isLensesOpen, setIsLensesOpen] = useState(true);
  const [isFilmOpen, setIsFilmOpen] = useState(true);

  const form = useForm<CameraFormValues>({
    resolver: zodResolver(cameraSchema),
    defaultValues: {
      brand: '',
      model: '',
      camera_type: 'dslr',
      format: null,
      mount: null,
      status: 'working',
      serial_number: '',
      purchase_date: '',
      purchase_price: '',
      notes: '',
      adapter_enabled: false,
      adapter_mounts: ''
    }
  });

  useEffect(() => {
    if (id) {
      fetchCameraAndLenses();
    }
  }, [id]);

  useEffect(() => {
    if (camera) {
      form.reset({
        brand: camera.brand,
        model: camera.model,
        camera_type: camera.camera_type,
        format: camera.format || null,
        mount: camera.mount || null,
        status: camera.status,
        serial_number: camera.serial_number || '',
        purchase_date: camera.purchase_date || '',
        purchase_price: camera.purchase_price ? camera.purchase_price.toString() : '',
        notes: camera.notes || '',
        adapter_enabled: camera.adapter_enabled || false,
        adapter_mounts: camera.adapter_mounts || ''
      });

      if (isFilmCamera(camera.camera_type)) {
        fetchCompatibleFilm(camera.format);
      } else {
        setCompatibleFilms([]);
      }
    }
  }, [camera, form]);

  const fetchCameraAndLenses = async () => {
    try {
      setLoading(true);
      setError(null);

      const { data: cameraData, error: cameraError } = await supabase
        .from('camera_inventory')
        .select('*')
        .eq('id', id)
        .single();

      if (cameraError) throw cameraError;
      if (!cameraData) throw new Error("Camera not found");
      
      const typedCameraData = cameraData as unknown as CameraInventory;
      setCamera(typedCameraData);
      
      if (typedCameraData.camera_type === 'point_and_shoot') {
        setCompatibleLenses([]);
        setLoading(false);
        return;
      }
      
      setHasAdapter(!!typedCameraData.adapter_enabled);
      
      let mountsArray: string[] = [];
      if (typedCameraData.adapter_mounts) {
        mountsArray = typedCameraData.adapter_mounts.split(',');
        setAdapterMounts(mountsArray);
      } else {
        setAdapterMounts([]);
      }
      
      let validMounts: string[] = [];
      
      if (typedCameraData.mount) {
        validMounts.push(typedCameraData.mount);
      }
      
      if (typedCameraData.adapter_enabled && mountsArray.length > 0) {
        validMounts = [...validMounts, ...mountsArray];
      }
      
      if (validMounts.length > 0) {
        const { data: lensData, error: lensError } = await supabase
          .from('lens_inventory')
          .select('*')
          .in('mount', validMounts);

        if (lensError) throw lensError;
        
        if (Array.isArray(lensData) && lensData.length > 0) {
          const typedLensData = safelyAssertType<LensInventory>(lensData);
          setCompatibleLenses(typedLensData);
        } else {
          setCompatibleLenses([]);
        }
      } else {
        setCompatibleLenses([]);
      }

      if (isFilmCamera(typedCameraData.camera_type)) {
        fetchCompatibleFilm(typedCameraData.format);
      }
    } catch (error: any) {
      console.error("Error fetching camera details:", error);
      setError(error.message || "Failed to load camera details");
      toast.error("Failed to load camera details");
    } finally {
      setLoading(false);
    }
  };

  const fetchCompatibleFilm = async (cameraFormat: string | null) => {
    try {
      const compatibleFilmType = getCompatibleFilmType(cameraFormat);
      
      if (!compatibleFilmType) {
        setCompatibleFilms([]);
        return;
      }
      
      let filmTypeCondition;
      if (compatibleFilmType === "120") {
        filmTypeCondition = ['120', '220'];
        const { data: filmData, error: filmError } = await supabase
          .from('film_inventory')
          .select('*')
          .in('film_type', filmTypeCondition)
          .neq('status', 'exposed'); // Filter out exposed film

        if (filmError) throw filmError;
        
        if (Array.isArray(filmData) && filmData.length > 0) {
          setCompatibleFilms(filmData as FilmInventory[]);
        } else {
          setCompatibleFilms([]);
        }
      } else {
        const { data: filmData, error: filmError } = await supabase
          .from('film_inventory')
          .select('*')
          .eq('film_type', compatibleFilmType)
          .neq('status', 'exposed'); // Filter out exposed film

        if (filmError) throw filmError;
        
        if (Array.isArray(filmData) && filmData.length > 0) {
          setCompatibleFilms(filmData as FilmInventory[]);
        } else {
          setCompatibleFilms([]);
        }
      }
    } catch (error: any) {
      console.error("Error fetching compatible film:", error);
      toast.error("Failed to load compatible film");
    }
  };

  const toggleAdapterCompatibility = async () => {
    try {
      if (!camera) return;
      
      const newHasAdapter = !hasAdapter;
      setHasAdapter(newHasAdapter);
      
      const { error } = await supabase
        .from('camera_inventory')
        .update({ 
          adapter_enabled: newHasAdapter,
          adapter_mounts: newHasAdapter ? (adapterMounts.length > 0 ? adapterMounts.join(',') : null) : null
        })
        .eq('id', camera.id);
        
      if (error) throw error;
      
      setCamera({
        ...camera,
        adapter_enabled: newHasAdapter,
        adapter_mounts: newHasAdapter ? (adapterMounts.length > 0 ? adapterMounts.join(',') : null) : null
      });
      
      toast.success(newHasAdapter 
        ? "Lens adapter compatibility enabled" 
        : "Lens adapter compatibility disabled");
      
      await handleLensUpdate();
    } catch (err: any) {
      console.error("Error updating adapter compatibility:", err);
      toast.error("Failed to update adapter compatibility");
    }
  };

  const handleAddAdapterMount = async (mountName: string) => {
    try {
      if (!camera) return;
      
      const sanitizedMount = mountName.replace(/[^a-zA-Z0-9]/g, '').slice(0, 4).toUpperCase();
      
      if (!sanitizedMount) {
        toast.error("Invalid mount name");
        return;
      }
      
      if (!adapterMounts.includes(sanitizedMount)) {
        const newAdapterMounts = [...adapterMounts, sanitizedMount];
        setAdapterMounts(newAdapterMounts);
        
        const { error } = await supabase
          .from('camera_inventory')
          .update({ 
            adapter_mounts: newAdapterMounts.join(',')
          })
          .eq('id', camera.id);
          
        if (error) throw error;
        
        setCamera({
          ...camera,
          adapter_mounts: newAdapterMounts.join(',')
        });
        
        toast.success(`Added adapter for ${sanitizedMount} mount`);
        
        await handleLensUpdate();
      }
    } catch (err: any) {
      console.error("Error adding adapter mount:", err);
      toast.error("Failed to add adapter mount");
    }
  };

  const handleRemoveAdapterMount = async (mountToRemove: string) => {
    try {
      if (!camera) return;
      
      const newAdapterMounts = adapterMounts.filter(mount => mount !== mountToRemove);
      setAdapterMounts(newAdapterMounts);
      
      const { error } = await supabase
        .from('camera_inventory')
        .update({ 
          adapter_mounts: newAdapterMounts.length > 0 ? newAdapterMounts.join(',') : null
        })
        .eq('id', camera.id);
        
      if (error) throw error;
      
      setCamera({
        ...camera,
        adapter_mounts: newAdapterMounts.length > 0 ? newAdapterMounts.join(',') : null
      });
      
      toast.success(`Removed adapter for ${mountToRemove} mount`);
      
      await handleLensUpdate();
    } catch (err: any) {
      console.error("Error removing adapter mount:", err);
      toast.error("Failed to remove adapter mount");
    }
  };

  const handleLensUpdate = async () => {
    try {
      if (!camera) return;
      
      if (camera.camera_type === 'point_and_shoot') {
        setCompatibleLenses([]);
        return;
      }
      
      let validMounts: string[] = [];
      
      if (camera.mount) {
        validMounts.push(camera.mount);
      }
      
      if (hasAdapter && adapterMounts.length > 0) {
        validMounts = [...validMounts, ...adapterMounts];
      }
      
      if (validMounts.length > 0) {
        const { data: lensData, error: lensError } = await supabase
          .from('lens_inventory')
          .select('*')
          .in('mount', validMounts);

        if (lensError) throw lensError;
        
        if (Array.isArray(lensData) && lensData.length > 0) {
          const typedLensData = safelyAssertType<LensInventory>(lensData);
          setCompatibleLenses(typedLensData);
        } else {
          setCompatibleLenses([]);
        }
      } else {
        setCompatibleLenses([]);
      }
    } catch (err: any) {
      console.error("Error updating lenses:", err);
      toast.error("Failed to refresh lens list");
    }
  };

  const handleUpdateCamera = async (values: CameraFormValues) => {
    try {
      if (!camera) return;
      
      const { error } = await supabase
        .from('camera_inventory')
        .update({
          brand: values.brand,
          model: values.model,
          camera_type: values.camera_type,
          format: values.format || null,
          mount: values.mount || null,
          status: values.status,
          serial_number: values.serial_number || null,
          purchase_date: values.purchase_date || null,
          purchase_price: values.purchase_price ? parseFloat(values.purchase_price) : null,
          notes: values.notes || null,
          adapter_enabled: values.adapter_enabled || false,
          adapter_mounts: values.adapter_mounts || null
        })
        .eq('id', camera.id);
        
      if (error) throw error;
      
      setIsEditDialogOpen(false);
      toast.success('Camera updated successfully');
      
      setCamera({
        ...camera,
        brand: values.brand,
        model: values.model,
        camera_type: values.camera_type,
        format: values.format || null,
        mount: values.mount || null,
        status: values.status,
        serial_number: values.serial_number || null,
        purchase_date: values.purchase_date || null,
        purchase_price: values.purchase_price ? parseFloat(values.purchase_price) : null,
        notes: values.notes || null,
        adapter_enabled: values.adapter_enabled || false,
        adapter_mounts: values.adapter_mounts || null
      });
      
      setHasAdapter(values.adapter_enabled || false);
      if (values.adapter_mounts) {
        setAdapterMounts(values.adapter_mounts.split(','));
      } else {
        setAdapterMounts([]);
      }
      
      await handleLensUpdate();

      if (isFilmCamera(values.camera_type)) {
        fetchCompatibleFilm(values.format);
      } else {
        setCompatibleFilms([]);
      }
    } catch (err: any) {
      console.error("Error updating camera:", err);
      toast.error("Failed to update camera");
    }
  };

  const formatFormat = (format: string | null) => {
    if (!format) return "N/A";
    if (format === 'aps-c') return 'APS-C';
    if (format === 'full_frame') return 'Full Frame';
    if (format === 'medium_format') return 'Medium Format';
    if (format === 'large_format') return 'Large Format';
    return format.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  const formatMount = (mount: string | null) => {
    if (!mount) return "N/A";
    if (mount === 'canon_ef') return 'Canon EF';
    if (mount === 'nikon_f') return 'Nikon F';
    if (mount === 'sony_e') return 'Sony E';
    if (mount === 'leica_m') return 'Leica M';
    if (mount === 'pentax_k') return 'Pentax K';
    if (mount === 'minolta_md') return 'Minolta MD';
    if (mount === 'olympus_om') return 'Olympus OM';
    if (mount === 'm42') return 'M42';
    if (mount === 'hasselblad') return 'Hasselblad';
    return mount.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  return {
    camera,
    compatibleLenses,
    compatibleFilms,
    loading,
    error,
    hasAdapter,
    setHasAdapter,
    adapterMounts,
    isEditDialogOpen,
    setIsEditDialogOpen,
    isLensesOpen,
    setIsLensesOpen,
    isFilmOpen,
    setIsFilmOpen,
    form,
    toggleAdapterCompatibility,
    handleAddAdapterMount,
    handleRemoveAdapterMount,
    handleLensUpdate,
    handleUpdateCamera,
    fetchCompatibleFilm,
    formatFormat,
    formatMount
  };
}
